import React, { useState, useMemo } from 'react';
import { MessageBundle } from '@amzn/arb-tools';
import { house, houseSolid } from '@amzn/storm-ui-icons';
import { Tooltip, useIsMobile } from '@amzn/storm-ui';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import {
  setLandingAppAction,
  unsetLandingAppAction,
} from 'src/commons/utils/setLandingApp';
import { BANNER_TYPE } from 'src/app/slice';
import {
  SnackbarContainer,
  ToastBanner,
} from 'src/features/header/components/ToastBanner/ToastBanner';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { LandingAppIconButton } from 'src/commons/components/LandingAppIcon/styles';
import { chromeComponents } from 'src/constants';

export const enum LANDING_PLACEMENT {
  HEADER,
  SIDENAV,
}

interface LandingIconProps {
  appName: string;
  landingAppName: string;
  tooltipLabel: string;
  landingPlacement: LANDING_PLACEMENT;
  tabIndex?: number;
  updateLandingAppName: (...args: any[]) => any;
}

interface ToastBannerProps {
  showToastBanner: boolean;
  toastBannerType?: BANNER_TYPE;
}

const getToastBannerMessage = (
  bundle: MessageBundle,
  isLandingApp: boolean,
  bannerType: BANNER_TYPE,
): string => {
  if (bannerType === BANNER_TYPE.ERROR) {
    if (isLandingApp) {
      return bundle.getMessage('sidenav-landing-page-unset-toast-banner-error');
    }
    return bundle.getMessage('sidenav-landing-page-set-toast-banner-error');
  }

  if (isLandingApp) {
    return bundle.getMessage('sidenav-landing-page-set-toast-banner-success');
  }
  return bundle.getMessage('sidenav-landing-page-unset-toast-banner-success');
};

export const LandingAppIcon: React.VFC<LandingIconProps> = ({
  appName,
  landingAppName,
  tooltipLabel,
  landingPlacement,
  tabIndex,
  updateLandingAppName,
}) => {
  const bundle = useSidenavBundle();
  const isMobile = useIsMobile();
  const [toastBanner, setToastBanner] = useState<ToastBannerProps>({
    showToastBanner: false,
    toastBannerType: undefined,
  });
  const [enableClick, setEnableClick] = useState(true);
  const isLandingApp = appName != '' && landingAppName === appName;
  const landingAppPlacement =
    landingPlacement === LANDING_PLACEMENT.HEADER
      ? chromeComponents.HEADER
      : chromeComponents.NAV;
  //TAKT delay emit onClick event, when TAKT emits onClick, landingApp has been set
  const taktOperation = !isLandingApp ? 'set' : 'unset';
  const landingAppId = `aac-${taktOperation}-landing-${landingAppPlacement}-${appName}`;

  const tooltipMessage = (isArialLabel: boolean) =>
    bundle.formatMessage(
      isLandingApp
        ? 'sidenav-landing-page-unset-label'
        : 'sidenav-landing-page-set-label',
      { application: isArialLabel ? tooltipLabel : '' },
    );

  const onClickEvent = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (!enableClick) return;
    setEnableClick(false);
    isLandingApp
      ? unsetLandingAppAction({
          updateLandingAppName,
          setToastBanner,
        })
      : setLandingAppAction({
          appName: appName,
          updateLandingAppName,
          setToastBanner,
        });
    // e.target is typed as EventTarget; the clicked node is an element with blur().
    (e.target as HTMLElement).blur();
    setEnableClick(true);
  };

  const landingIcon = useMemo(
    () => (
      <LandingAppIconButton
        id={landingAppId}
        variant="transparent"
        icon={isLandingApp ? houseSolid : house}
        onClick={onClickEvent}
        tabIndex={tabIndex}
        role="switch"
        aria-checked={isLandingApp ? 'true' : 'false'}
        label={tooltipMessage(true)}
        placement={landingPlacement}
        data-ccx-e2e-id={landingAppId}
        {...getTaktAttributes(landingAppId, landingAppPlacement)}
      />
    ),
    [
      landingAppId,
      isLandingApp,
      onClickEvent,
      tabIndex,
      tooltipMessage,
      landingPlacement,
    ],
  );

  return (
    <>
      {isMobile ? (
        landingIcon
      ) : (
        <Tooltip
          trigger={landingIcon}
          message={tooltipMessage(false)}
          nonInteractive
          transitionDuration={0}
        />
      )}
      {toastBanner.showToastBanner && toastBanner.toastBannerType && (
        <SnackbarContainer>
          <ToastBanner
            banner={{
              id: 'landing-experience-toast-banner',
              message: getToastBannerMessage(
                bundle,
                isLandingApp,
                toastBanner.toastBannerType,
              ),
              type: toastBanner.toastBannerType,
              withCloseButton: false,
            }}
            bannerClosed={() => {
              setToastBanner({
                showToastBanner: false,
                toastBannerType: undefined,
              });
            }}
            autoClose={true}
          />
        </SnackbarContainer>
      )}
    </>
  );
};
